// Preview side of the member emails. This mirrors the paragraph rendering and
// the house shell the transactional pipe uses, so what an admin sees in the
// content editor is what a member receives.

import { sanitizeDocumentContent } from "@/lib/sanitize";
import { MESSAGE_HEADINGS } from "@/lib/messagePreview";

/** Sample values so a preview never shows raw braces. */
export const PREVIEW_VARS: Record<string, string> = {
  first_name: "Alex",
  una_name: "Cornerstone Stewardship Association",
  email: "alex@example.com",
  portal_url: "https://wlthystwrd.com",
  action_url: "https://wlthystwrd.com/welcome",
  tracking_number: "123456789012",
  note: "Here is a short note from the team.",
  association_amount: "14500",
  monthly_amount: "250",
  fedex_tracking: "123456789012",
  ein_scheduled_for: "Mar 4, 2026",
  ein_acquired_on: "Mar 6, 2026",
  ca_submitted_on: "Mar 9, 2026",
  ca_approved_on: "Mar 18, 2026",
  docs_sent_on: "Mar 20, 2026",
  fedex_delivered_on: "Mar 23, 2026",
  packet_received_on: "Mar 23, 2026",
};

const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/** Fills sample values in. Unknown spots render as nothing, same as the pipe. */
export function fillPreviewTokens(text: string, vars: Record<string, string> = PREVIEW_VARS): string {
  return (text || "").replace(TOKEN_RE, (_m, name: string) => vars[name] ?? "");
}

/** Same paragraph rendering the sending code uses. */
export function markdownParagraphs(body: string): string[] {
  return (body || "")
    .split(/\n{2,}|\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      let html = line
        .replace(/^#{1,6}\s*/, "")
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
      if (/^#{1,6}\s/.test(line)) html = `<strong>${html}</strong>`;
      return html;
    });
}

/** Same shell the sending code uses. */
export function emailShell(heading: string, paragraphs: string[]): string {
  const body = paragraphs
    .map((p) => `<p style="margin:0 0 14px;line-height:1.6">${p}</p>`)
    .join("");
  return `<div style="font-family:Georgia,serif;color:#1f2937;max-width:560px">
  <h1 style="font-size:20px;margin:0 0 16px">${heading}</h1>
  ${body}
  <p style="margin-top:24px;font-size:13px;color:#6b7280">Wealthy Steward</p>
</div>`;
}

/** Links in a preview open in a new tab rather than moving the admin page. */
function openInNewTab(html: string): string {
  return html.replace(/<a\s/gi, '<a target="_blank" ');
}

/** The hidden line an inbox shows next to the subject. Mirrors the pipe. */
export function withPreheader(html: string, preheader?: string | null): string {
  const line = (preheader || "").trim();
  if (!line) return html;
  const safe = line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;height:0;width:0">${safe}</div>${html}`;
}

/**
 * Renders the wording exactly the way the recipient's email client would show it.
 * The result is sanitized and safe to place in the page.
 */
export function renderEmailPreview(
  slug: string,
  body: string,
  title?: string,
  preheader?: string | null,
  vars: Record<string, string> = PREVIEW_VARS,
): string {
  const heading = MESSAGE_HEADINGS[slug] ?? title ?? "";
  const paragraphs = markdownParagraphs(body).map((p) => fillPreviewTokens(p, vars));
  const html = withPreheader(
    emailShell(fillPreviewTokens(heading, vars), paragraphs),
    fillPreviewTokens(preheader || "", vars),
  );
  return sanitizeDocumentContent(openInNewTab(html));
}

